import { useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from './hooks';
import { 
  selectSidebarOpen, 
  selectSidebarExpanded, 
  selectIsMobile,
  toggleSidebar,
  openSidebar,
  closeSidebar,
  setSidebarOpen,
  toggleSidebarExpanded,
  setSidebarExpanded,
  setMobileView
} from '../slices/uiSlice';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

/**
 * Enhanced Redux hook for sidebar management
 * Replaces SidebarContext with Redux-based state management
 */
export const useSidebar = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobileBreakpoint = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Get state from Redux
  const sidebarOpen = useAppSelector(selectSidebarOpen);
  const sidebarExpanded = useAppSelector(selectSidebarExpanded);
  const isMobile = useAppSelector(selectIsMobile);

  // Update mobile state when breakpoint changes
  useEffect(() => {
    dispatch(setMobileView(isMobileBreakpoint));
  }, [isMobileBreakpoint, dispatch]);

  // Auto-collapse on mobile if needed
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      dispatch(closeSidebar());
    }
  }, [isMobile, sidebarOpen, dispatch]);

  // Keyboard shortcut support (Ctrl + B)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault();
        dispatch(toggleSidebar());
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dispatch]);

  // Actions
  const toggleSidebarAction = useCallback(() => {
    dispatch(toggleSidebar());
  }, [dispatch]);

  const openSidebarAction = useCallback(() => {
    dispatch(openSidebar());
  }, [dispatch]);

  const closeSidebarAction = useCallback(() => {
    dispatch(closeSidebar());
  }, [dispatch]);

  const setSidebarOpenAction = useCallback((open) => {
    dispatch(setSidebarOpen(open));
  }, [dispatch]);

  const toggleSidebarExpandedAction = useCallback(() => {
    dispatch(toggleSidebarExpanded());
  }, [dispatch]);

  const setSidebarExpandedAction = useCallback((expanded) => {
    dispatch(setSidebarExpanded(expanded));
  }, [dispatch]);

  return {
    // State
    sidebarOpen,
    sidebarExpanded,
    isMobile,
    
    // Actions
    toggleSidebar: toggleSidebarAction,
    openSidebar: openSidebarAction,
    closeSidebar: closeSidebarAction,
    setSidebarOpen: setSidebarOpenAction,
    toggleSidebarExpanded: toggleSidebarExpandedAction,
    setSidebarExpanded: setSidebarExpandedAction,
  };
};
